'use client';
import { useCallback, useSyncExternalStore } from 'react';

type UseMediaQueryOptions = {
  /**
   * Default for SSR and hydration
   * @default false
   */
  defaultMatches?: boolean;
};

/**
 * Custom hook which returns the result of the provided media `query`, watching for changes
 * @param query `query` passed to `window.matchMedia(query)`
 * @param [options.defaultMatches] default value for SSR and hydration
 */
function useMediaQuery(query: string, { defaultMatches = false }: UseMediaQueryOptions = {}): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      // This is necessary due to a bug in Safari where unsubscribing from a MediaQueryList does not work
      const controller = new AbortController();

      const onUpdate = () => {
        /* istanbul ignore if */
        if (controller.signal.aborted) return;
        onStoreChange();
      };

      const list = window.matchMedia(query);

      // NOTE: using `addListener` (deprecated) instead of `addEventListener` for Safari <14 support
      list.addListener(onUpdate);

      return () => {
        list.removeListener(onUpdate);
        controller.abort();
      };
    },
    [query]
  );

  const getSnapshot = useCallback(() => {
    return window.matchMedia(query).matches;
  }, [query]);

  const getServerSnapshot = useCallback(() => defaultMatches, [defaultMatches]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

export { useMediaQuery, type UseMediaQueryOptions };
